import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SupplierVehicleDocuments.css";
import SupplierSidebar from "./SupplierSidebar";
import SupplierTopbar from "./SupplierTopbar";
import supplierPortalService from "../../services/supplierPortalService";
const PUBLIC_BACKEND_URL = "https://srinivasafleet.com";
const getVehicleNumber = (vehicle) => {
  if (!vehicle) return "";
  const possibleValues = [
    vehicle.registration_number,
    vehicle.registration_no,
    vehicle.vehicle_number,
    vehicle.vehicle_no,
    vehicle.registration,
    vehicle.reg_no,
    vehicle.reg_number,
    vehicle.vehicle_registration_number,
    vehicle.vehicle_registration_no,
    vehicle.display_number,
    vehicle.display_name,
    vehicle.vehicle?.registration_number,
    vehicle.vehicle?.registration_no,
    vehicle.vehicle?.vehicle_number,
    vehicle.vehicle?.vehicle_no,
    vehicle.vehicle?.registration,
    vehicle.vehicle?.reg_no,
    vehicle.vehicle?.reg_number,
  ];
  const value = possibleValues.find(
    (item) =>
      item !== undefined &&
      item !== null &&
      String(item).trim() !== ""
  );
  return value ? String(value).trim() : "";
};
const getVehicleLabel = (vehicle) => {
  const number = getVehicleNumber(vehicle);
  if (number) {
    return number;
  }
  return `Vehicle ${vehicle?.id || ""}`;
};
const getDocumentName = (document) => {
  if (!document) {
    return "Vehicle Document";
  }
  return (
    document.label ||
    document.document_type ||
    document.document_name ||
    document.name ||
    document.type ||
    "Vehicle Document"
  );
};
const getDocumentIcon = (name) => {
  const value = String(name || "").toLowerCase();
  if (
    value.includes("rc") ||
    value.includes("registration")
  ) {
    return "▤";
  }
  if (value.includes("permit")) {
    return "▥";
  }
  if (value.includes("fitness")) {
    return "✓";
  }
  if (value.includes("insurance")) {
    return "▣";
  }
  if (value.includes("pollution")) {
    return "♢";
  }
  if (value.includes("tax")) {
    return "₹";
  }
  if (value.includes("police")) {
    return "▦";
  }
  return "▤";
};
const getPublicFileUrl = (fileUrl) => {
  if (!fileUrl) {
    return "";
  }
  const value = String(fileUrl).trim();
  if (!value) {
    return "";
  }
  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    if (
      value.includes("localhost") ||
      value.includes("127.0.0.1")
    ) {
      try {
        const parsedUrl = new URL(value);
        return `${PUBLIC_BACKEND_URL}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      } catch (error) {
        return PUBLIC_BACKEND_URL;
      }
    }
    return value;
  }
  if (value.startsWith("//")) {
    return `https:${value}`;
  }
  if (value.startsWith("/")) {
    return `${PUBLIC_BACKEND_URL}${value}`;
  }
  return `${PUBLIC_BACKEND_URL}/${value}`;
};
const getDownloadFileName = (item) => {
  const documentName = getDocumentName(item)
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  const fileUrl = getPublicFileUrl(
    item?.file_url
  );
  let extension = "pdf";
  try {
    const parsedUrl = new URL(fileUrl);
    const pathname = parsedUrl.pathname || "";
    const lastPart =
      pathname.split("/").pop() || "";
    const match =
      lastPart.match(
        /\\.([a-zA-Z0-9]+)$/
      );
    if (match?.[1]) {
      extension = match[1];
    }
  } catch (error) {
    void error;
  }
  return `${documentName || "Vehicle_Document"
    }.${extension}`;
};
const SupplierVehicleDocuments = () => {
  const navigate = useNavigate();
  const [supplier, setSupplier] =
    useState(null);
  const [vehicles, setVehicles] =
    useState([]);
  const [selectedVehicle, setSelectedVehicle] =
    useState("");
  const [years, setYears] =
    useState([]);
  const [selectedYear, setSelectedYear] =
    useState("");
  const [months, setMonths] =
    useState([]);
  const [selectedMonth, setSelectedMonth] =
    useState("");
  const [documents, setDocuments] =
    useState([]);
  const [
    selectedVehicleInfo,
    setSelectedVehicleInfo,
  ] = useState(null);
  const [loading, setLoading] =
    useState(true);
  const [
    documentsLoading,
    setDocumentsLoading,
  ] = useState(false);
  const [errorMessage, setErrorMessage] =
    useState("");
  const [sidebarOpen, setSidebarOpen] =
    useState(false);
  const [vehicleNames, setVehicleNames] =
    useState({});
  useEffect(() => {
    let mounted = true;
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        if (
          !supplierPortalService.isLoggedIn()
        ) {
          navigate("/supplier/login", {
            replace: true,
          });
          return;
        }
        const [
          profileResponse,
          vehiclesResponse,
        ] = await Promise.all([
          supplierPortalService.getMe(),
          supplierPortalService.getVehicles(),
        ]);
        if (!mounted) {
          return;
        }
        const profile =
          profileResponse?.supplier ||
          profileResponse ||
          null;
        const vehicleList =
          vehiclesResponse?.vehicles ||
          vehiclesResponse?.data ||
          [];
        const safeVehicles =
          Array.isArray(vehicleList)
            ? vehicleList
            : [];
        setSupplier(profile);
        setVehicles(safeVehicles);
        const names = {};
        safeVehicles.forEach((vehicle) => {
          const number =
            getVehicleNumber(vehicle);
          if (
            vehicle?.id &&
            number
          ) {
            names[String(vehicle.id)] =
              number;
          }
        });
        setVehicleNames(names);
        if (safeVehicles.length) {
          setSelectedVehicle(
            String(
              safeVehicles[0].id
            )
          );
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        if (
          !supplierPortalService.isLoggedIn()
        ) {
          navigate("/supplier/login", {
            replace: true,
          });
          return;
        }
        setErrorMessage(
          error?.message ||
          "Unable to load vehicle documents."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadInitialData();
    return () => {
      mounted = false;
    };
  }, [navigate]);
  useEffect(() => {
    if (!selectedVehicle) {
      setYears([]);
      setSelectedYear("");
      setMonths([]);
      setSelectedMonth("");
      setDocuments([]);
      setSelectedVehicleInfo(null);
      return;
    }
    let mounted = true;
    const loadVehiclePeriod = async () => {
      try {
        setErrorMessage("");
        setDocuments([]);
        setMonths([]);
        setSelectedMonth("");
        const response =
          await supplierPortalService.getVehicleYears(
            selectedVehicle
          );
        if (!mounted) {
          return;
        }
        const yearList =
          Array.isArray(
            response?.years
          )
            ? response.years
            : [];
        setYears(yearList);
        const info =
          vehicles.find(
            (item) =>
              String(item.id) ===
              String(selectedVehicle)
          );
        setSelectedVehicleInfo(
          info || null
        );
        const number =
          getVehicleNumber(info);
        if (number) {
          setVehicleNames(
            (previous) => ({
              ...previous,
              [String(selectedVehicle)]:
                number,
            })
          );
        }
        if (yearList.length) {
          setSelectedYear(
            String(yearList[0])
          );
        } else {
          setSelectedYear("");
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        setErrorMessage(
          error?.message ||
          "Unable to load vehicle years."
        );
      }
    };
    loadVehiclePeriod();
    return () => {
      mounted = false;
    };
  }, [
    selectedVehicle,
    vehicles,
  ]);
  useEffect(() => {
    if (
      !selectedVehicle ||
      !selectedYear
    ) {
      setMonths([]);
      setSelectedMonth("");
      return;
    }
    let mounted = true;
    const loadVehicleMonths = async () => {
      try {
        setErrorMessage("");
        setDocuments([]);
        setSelectedMonth("");
        const response =
          await supplierPortalService.getVehicleMonths(
            selectedVehicle,
            selectedYear
          );
        if (!mounted) {
          return;
        }
        const monthList =
          Array.isArray(
            response?.months
          )
            ? response.months
            : [];
        setMonths(monthList);
        if (monthList.length) {
          setSelectedMonth(
            String(monthList[0])
          );
        } else {
          setSelectedMonth("");
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        setErrorMessage(
          error?.message ||
          "Unable to load vehicle months."
        );
      }
    };
    loadVehicleMonths();
    return () => {
      mounted = false;
    };
  }, [
    selectedVehicle,
    selectedYear,
  ]);
  useEffect(() => {
    if (
      !selectedVehicle ||
      !selectedYear ||
      !selectedMonth
    ) {
      setDocuments([]);
      return;
    }
    let mounted = true;
    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        setErrorMessage("");
        const response =
          await supplierPortalService.getVehicleDocuments(
            selectedVehicle,
            selectedYear,
            selectedMonth
          );
        if (!mounted) {
          return;
        }
        const documentList =
          Array.isArray(
            response?.documents
          )
            ? response.documents
            : [];
        setDocuments(documentList);
        if (response?.vehicle) {
          setSelectedVehicleInfo(
            response.vehicle
          );
          const number =
            getVehicleNumber(
              response.vehicle
            );
          if (number) {
            setVehicleNames(
              (previous) => ({
                ...previous,
                [String(selectedVehicle)]:
                  number,
              })
            );
          }
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        setDocuments([]);
        setErrorMessage(
          error?.message ||
          "Unable to load vehicle documents."
        );
      } finally {
        if (mounted) {
          setDocumentsLoading(false);
        }
      }
    };
    loadDocuments();
    return () => {
      mounted = false;
    };
  }, [
    selectedVehicle,
    selectedYear,
    selectedMonth,
  ]);
  const currentVehicleNumber =
    getVehicleNumber(
      selectedVehicleInfo
    ) ||
    vehicleNames[
    String(selectedVehicle)
    ] ||
    "";
  const openDocument = (item) => {
    if (!item?.file_url) {
      return;
    }
    const fileUrl =
      getPublicFileUrl(
        item.file_url
      );
    if (!fileUrl) {
      return;
    }
    window.open(
      fileUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };
  const downloadDocument = async (
    item
  ) => {
    if (!item?.file_url) {
      return;
    }
    const fileUrl =
      getPublicFileUrl(
        item.file_url
      );
    if (!fileUrl) {
      return;
    }
    const fileName =
      getDownloadFileName(item);
    try {
      const response =
        await fetch(fileUrl, {
          method: "GET",
        });
      if (!response.ok) {
        throw new Error(
          "Download failed"
        );
      }
      const blob =
        await response.blob();
      const blobUrl =
        window.URL.createObjectURL(
          blob
        );
      const link =
        document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(
        blobUrl
      );
    } catch (error) {
      const link =
        document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel =
        "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  if (loading) {
    return (
      <div className="supplier-dashboard">
        <SupplierSidebar
          open={sidebarOpen}
          onClose={() =>
            setSidebarOpen(false)
          }
        />
        <div className="supplier-main">
          <SupplierTopbar
            supplier={supplier}
            sidebarOpen={sidebarOpen}
            onMenuClick={() =>
              setSidebarOpen(
                (value) => !value
              )
            }
          />
          <main className="supplier-dashboard-content">
            <div className="supplier-documents-loading">
              <div className="supplier-loading-spinner" />
              <p>
                Loading vehicle documents...
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }
  return (
    <div className="supplier-dashboard">
      <SupplierSidebar
        open={sidebarOpen}
        onClose={() =>
          setSidebarOpen(false)
        }
      />
      <div className="supplier-main">
        <SupplierTopbar
          supplier={supplier}
          sidebarOpen={sidebarOpen}
          onMenuClick={() =>
            setSidebarOpen(
              (value) => !value
            )
          }
        />
        <main className="supplier-dashboard-content">
          {errorMessage && (
            <div className="supplier-documents-error">
              {errorMessage}
            </div>
          )}
          <section className="supplier-documents-card">
            <div className="supplier-documents-toolbar supplier-documents-toolbar-single">
              <div className="supplier-filter-field supplier-vehicle-field">
                <label htmlFor="vehicle-select">
                  VEHICLE NUMBER
                </label>
                <select
                  id="vehicle-select"
                  value={selectedVehicle}
                  onChange={(event) => {
                    setSelectedVehicle(
                      event.target.value
                    );
                  }}
                  disabled={!vehicles.length}
                >
                  {vehicles.map(
                    (vehicle) => {
                      const id =
                        String(
                          vehicle.id
                        );
                      const label =
                        vehicleNames[id] ||
                        getVehicleNumber(
                          vehicle
                        ) ||
                        getVehicleLabel(
                          vehicle
                        );
                      return (
                        <option
                          key={vehicle.id}
                          value={vehicle.id}
                        >
                          {label}
                        </option>
                      );
                    }
                  )}
                </select>
              </div>
            </div>
            {currentVehicleNumber && (
              <div className="supplier-selected-vehicle">
                <span>
                  VEHICLE
                </span>
                <strong>
                  {currentVehicleNumber}
                </strong>
              </div>
            )}
            {documentsLoading ? (
              <div className="supplier-documents-loading">
                <div className="supplier-loading-spinner" />
                <p>
                  Loading documents...
                </p>
              </div>
            ) : documents.length ===
              0 ? (
              <div className="supplier-documents-empty">
                <div className="supplier-empty-icon">
                  ▤
                </div>
                <h3>
                  No documents found
                </h3>
                <p>
                  No vehicle documents are
                  available for the selected
                  vehicle.
                </p>
              </div>
            ) : (
              <div className="supplier-documents-grid">
                {documents.map(
                  (item, index) => {
                    const documentName =
                      getDocumentName(
                        item
                      );
                    const hasFile =
                      Boolean(
                        item?.file_url
                      );
                    return (
                      <article
                        className="supplier-document-card"
                        key={
                          item?.id ||
                          `${documentName}-${index}`
                        }
                      >
                        <div className="supplier-document-top">
                          <div className="supplier-document-icon">
                            {getDocumentIcon(
                              documentName
                            )}
                          </div>
                          <span
                            className={
                              hasFile
                                ? "document-status available"
                                : "document-status unavailable"
                            }
                          >
                            {hasFile
                              ? "AVAILABLE"
                              : "UNAVAILABLE"}
                          </span>
                        </div>
                        <div className="supplier-document-info">
                          <span className="document-category">
                            VEHICLE DOCUMENT
                          </span>
                          <h3>
                            {documentName}
                          </h3>
                          <p>
                            {hasFile
                              ? "Document available"
                              : "Document unavailable"}
                          </p>
                        </div>
                        <div
                          className="supplier-document-actions"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          <button
                            type="button"
                            className="supplier-document-view-btn"
                            disabled={!hasFile}
                            onClick={() => openDocument(item)}
                          >
                            <span className="view-document-desktop">
                              {hasFile ? "View Document" : "Unavailable"}
                            </span>
                            <span className="view-document-mobile">
                              {hasFile ? "View" : "Unavailable"}
                            </span>
                          </button>
                          <button
                            type="button"
                            className="supplier-document-download-btn"
                            disabled={!hasFile}
                            onClick={() =>
                              downloadDocument(
                                item
                              )
                            }
                            style={{
                              flex: "1 1 0",
                              width: "50%",
                              minWidth: 0,
                              margin: 0,
                            }}
                          >
                            Download
                          </button>
                        </div>
                      </article>
                    );
                  }
                )}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};
export default SupplierVehicleDocuments;
